import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';

/** The scope stages an agent can narrate progress against (mirrors `HandoffStep`). */
export type ProgressStage = 'questions' | 'review' | 'documents' | 'backlog';

/**
 * Who a narration is attributed to (mirrors the api's `ScopeProgressActor`, PLAN-99):
 * the acting agent (null when a person acts without one) and the person it acts for.
 * The log header renders "<agent> via <person>" from this, not from `preflight.agents[0]`
 * — so a teammate's agent is never shown under the viewer's own agent's name.
 */
export interface ScopeProgressActor {
	agent: string | null;
	person: string;
}

/**
 * One `scope.progress` narration an agent emitted while working a scope over MCP
 * (mirrors the api's `ScopeProgress`). It persists nothing — it's the live event
 * log the hand-off modal renders so the human sees the agent working, not a bare
 * spinner. The real data write still ships `scope.updated` and drives navigation.
 */
export interface ScopeProgress {
	scopeId: string;
	stage: ProgressStage;
	label: string;
	questions?: number;
	sections?: number;
	/** Who this step is attributed to (PLAN-99): the acting agent + the person. */
	actor?: ScopeProgressActor;
	/** ISO 8601, set by the api at emit time. */
	at: string;
}

/** The most recent narrations we keep per scope stage (older ones fall off the tail). */
const MAX_PROGRESS = 20;

/**
 * The react-query cache key holding one scope stage's live progress log. Keyed by
 * `scopeId + stage` (PLAN-97): a run belongs to the stage it's advancing, so closing
 * the hand-off no longer forgets it and a review run doesn't clobber a questions run.
 */
export const scopeProgressKey = (scopeId: string, stage: ProgressStage) =>
	['scope-progress', scopeId, stage] as const;

/**
 * Append one narration to its scope stage's progress log, capped to the last
 * {@link MAX_PROGRESS}. Shared by the root realtime reconciler (the only writer of this
 * cache). Unversioned — progress is append-only, and an agent's event carries no
 * mutationId to echo-drop.
 */
export function appendScopeProgress(client: QueryClient, entry: ScopeProgress): void {
	client.setQueryData<ScopeProgress[]>(scopeProgressKey(entry.scopeId, entry.stage), (prev = []) =>
		[...prev, entry].slice(-MAX_PROGRESS),
	);
}

/** Drop a scope stage's progress log, so a fresh hand-off starts from an empty timeline. */
export function clearScopeProgress(
	client: QueryClient,
	scopeId: string,
	stage: ProgressStage,
): void {
	client.setQueryData<ScopeProgress[]>(scopeProgressKey(scopeId, stage), []);
}

/**
 * Read a scope stage's live progress log (default `[]`). The cache is fed by the root
 * realtime reconciler; this hook just observes it, so the hand-off modal re-renders as
 * each narration lands, and survives the modal closing and reopening (PLAN-97).
 */
export function useScopeProgress(scopeId: string, stage: ProgressStage): ScopeProgress[] {
	const { data } = useQuery<ScopeProgress[]>({
		queryKey: scopeProgressKey(scopeId, stage),
		// Purely a realtime sink: nothing to fetch, so it never runs a queryFn.
		queryFn: () => [],
		enabled: false,
		initialData: [],
	});
	return data ?? [];
}

/** Clear a scope stage's progress log imperatively (e.g. once its write lands). */
export function useClearScopeProgress(): (scopeId: string, stage: ProgressStage) => void {
	const client = useQueryClient();
	return (scopeId: string, stage: ProgressStage) => clearScopeProgress(client, scopeId, stage);
}

/** Format a narration's ISO `at` as a local HH:MM:SS clock, for the timeline. */
export function formatProgressTime(at: string): string {
	const date = new Date(at);
	if (Number.isNaN(date.getTime())) {
		return '';
	}
	const pad = (value: number) => String(value).padStart(2, '0');
	return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}
